import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { Company, GetCompanyPayload, PaginatedResponse, User } from '@interfaces';
import { environment } from '@environment';
import { CreateManagerOpts, ManagerOfCompany, SearchManagersOfCompanyOpts } from 'src/app/_interfaces/manager';

@Injectable({
  providedIn: 'root',
})
export class CompanyHttpService {
  private API_URL = environment.API_URL;
  private defaultOpts = { withCredentials: true };

  constructor(private http: HttpClient) {}

  public getCompanies(payload: GetCompanyPayload): Observable<PaginatedResponse<Company>> {
    const url = `${this.API_URL}/companies/search`;
    return this.http.post<PaginatedResponse<Company>>(url, payload);
  }

  public getCompaniesBasicDataList(payload: GetCompanyPayload): Observable<PaginatedResponse<Company>> {
    const url = `${this.API_URL}/companies/search/basic-data`;
    return this.http.post<PaginatedResponse<Company>>(url, payload);
  }

  public getCompanyBasicData(payload: { id: string }): Observable<Company> {
    const url = `${this.API_URL}/companies/` + payload.id;
    return this.http.get<Company>(url, { params: { scope: 'basic-data' } });
  }

  public getCompany(companyId: string): Observable<Company> {
    const url = `${this.API_URL}/companies/${companyId}`;
    return this.http.get<Company>(url);
  }

  public createCompany(company: Company): Observable<Company> {
    const url = `${this.API_URL}/companies`;
    return this.http.post<Company>(url, company);
  }

  public updateCompany(companyId: string, company: Company): Observable<Company> {
    const url = `${this.API_URL}/companies/${companyId}`;
    return this.http.patch<Company>(url, company, this.defaultOpts);
  }

  public removeCompany(companyId: string): Observable<{ success: boolean }> {
    const url = `${this.API_URL}/companies/${companyId}`;
    return this.http.delete<{ success: boolean }>(url, this.defaultOpts);
  }

  public setIsActiveCompany(companyId: string, isActive: boolean): Observable<Company> {
    const url = `${this.API_URL}/companies/${companyId}/${isActive}`;
    return this.http.get<Company>(url);
  }

  public searchManagersOfCompany(opts: SearchManagersOfCompanyOpts): Observable<PaginatedResponse<Partial<User>>> {
    const url = `${this.API_URL}/users/managers/search`;
    return this.http.post<PaginatedResponse<Partial<User>>>(url, opts);
  }

  public createManager(payload: CreateManagerOpts): Observable<{ manager: Partial<User>; emailSent: boolean }> {
    const url = `${this.API_URL}/users/managers`;
    return this.http.post<{ manager: Partial<User>; emailSent: boolean }>(url, payload);
  }
  public updateManager(payload: Partial<User>, userId: string): Observable<User> {
    const url = `${this.API_URL}/users/managers/${userId}`;
    return this.http.patch<User>(url, payload);
  }
  public softRemoveManager(userId: string): Observable<Partial<User>> {
    const url = `${this.API_URL}/users/managers/${userId}`;
    return this.http.delete<Partial<User>>(url);
  }

  public restoreManager(managerId: string): Observable<Partial<User>> {
    const url = `${this.API_URL}/users/managers/${managerId}/restore`;
    return this.http.patch<Partial<User>>(url, {});
  }

  public resendManagerActivation(email: string, userId: string): Observable<{ emailSent: boolean }> {
    const url = `${this.API_URL}/users/managers/${userId}/resend-activation`;
    return this.http.post<{ emailSent: boolean }>(url, { email });
  }
  public getManagerDetails(userId: string): Observable<Partial<User>> {
    const url = `${this.API_URL}/users/managers/${userId}`;
    return this.http.get<Partial<User>>(url);
  }

  public getAllCompaniesManagedBy(managerId: string): Observable<ManagerOfCompany[]> {
    const url = `${this.API_URL}/users/managers/${managerId}/managed-companies`;
    return this.http.get<ManagerOfCompany[]>(url);
  }

  public addManagedCompany({
    managerId,
    companyId,
  }: {
    managerId: string;
    companyId: string;
  }): Observable<Partial<ManagerOfCompany>> {
    const url = `${this.API_URL}/users/managers/${managerId}/managed-companies`;
    return this.http.post<Partial<ManagerOfCompany>>(url, { companyId });
  }
  public removeManagedCompany({
    managerId,
    mocId,
  }: {
    managerId: string;
    mocId: string;
  }): Observable<Partial<ManagerOfCompany>> {
    const url = `${this.API_URL}/users/managers/${managerId}/managed-companies/${mocId}`;
    return this.http.delete<Partial<ManagerOfCompany>>(url);
  }

  public getCompanyOfSelfRegistered(): Observable<Partial<Company>> {
    const url = `${this.API_URL}/companies/hardcoded/of/self-registered`;
    return this.http.get<Partial<Company>>(url);
  }

  public getAllActiveCompanies(): Observable<Partial<Company>[]> {
    const url = `${this.API_URL}/companies/ALL/active/basic-data`;
    return this.http.get<Partial<Company>[]>(url);
  }
}
